import Navbar from './components/Navbar';
import Hero from './components/Hero';
import CategorySection from './components/CategorySection';
import PillarArticles from './components/PillarArticles';
import Footer from './components/Footer';
import { useEffect } from 'react';

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.help';
    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Basetao Sheet Help",
      "url": siteUrl,
      "description": "The ultimate Basetao Spreadsheet resource center for international shoppers.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${siteUrl}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    });
    document.head.appendChild(websiteScript);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What exactly is the Basetao Spreadsheet?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Basetao spreadsheet is a curated document that organizes product listings, prices, warehouse QC photos, and shipping estimates from Chinese marketplaces into one searchable resource. It helps international shoppers discover products efficiently without browsing hundreds of individual store pages."
          }
        },
        {
          "@type": "Question",
          "name": "Is the Basetao Spreadsheet free to use?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, the spreadsheet itself is freely accessible. You can browse product listings, compare prices, and view QC photos without any cost. The only expenses are the actual product purchases and shipping fees when you decide to buy."
          }
        },
        {
          "@type": "Question",
          "name": "Is Basetao legit and safe to use?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Basetao is a legitimate shopping agent service that has served thousands of international customers. They provide warehouse storage, QC photo verification, and multiple secure shipping options. All transactions are protected through standard payment channels."
          }
        },
        {
          "@type": "Question",
          "name": "How much does Basetao shipping cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Basetao shipping costs depend on package weight, destination country, and shipping method. Generally, shipping to the US and Europe ranges from $15 to $60 per kilogram depending on the speed and carrier you choose."
          }
        },
        {
          "@type": "Question",
          "name": "How do I find the best products on the Basetao spreadsheet?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Start by selecting a category that interests you, then browse the product listings with QC photos. Pay attention to price comparisons, sizing notes, and user feedback. The spreadsheet is organized to make filtering by category, price range, and popularity straightforward."
          }
        },
        {
          "@type": "Question",
          "name": "Does Basetao offer discount codes or coupons?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, Basetao occasionally offers promotional discounts and shipping coupons. These are typically announced on their platform and through partner channels. The spreadsheet often includes notes about current promotions when available."
          }
        }
      ]
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
      document.head.removeChild(websiteScript);
    };
  }, []);

  return (
    <main>
      <Navbar />
      <Hero />

      <section className="relative w-full py-12 md:py-16 bg-background-50 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <img
            src="https://readdy.ai/api/search-image?query=Ultra%20subtle%20delicate%20organic%20micro%20pattern%20with%20barely%20visible%20soft%20rounded%20shapes%20in%20extremely%20light%20warm%20tones%2C%20gentle%20flowing%20abstract%20texture%2C%20clean%20minimalist%20design%2C%20airy%20atmospheric%20background%2C%20no%20distinct%20objects%2C%20pure%20ambient%20subtle%20paper%20like%20texture%20for%20website&width=1400&height=600&seq=why-section-bg-v3&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              What Is Basetao Spreadsheet?
            </h2>
            <p className="text-foreground-600 text-base max-w-3xl mx-auto leading-relaxed">
              The Basetao spreadsheet is a powerful organizational tool used by thousands of international shoppers to browse, compare, and track products from Chinese marketplaces. It consolidates product listings, pricing data, warehouse quality check photos, and shipping estimates into a single, easy-to-navigate document — helping you make smarter purchasing decisions without the overwhelm.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white border-2 border-background-200/80 rounded-xl p-6 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-search-eye-line text-primary-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">Discover Products Faster</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Instead of browsing countless store pages one by one, the spreadsheet organizes hundreds of product listings with direct links, pricing, and sizing details in a searchable format.</p>
              </div>
            </div>
            <div className="bg-white border-2 border-background-200/80 rounded-xl p-6 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-scales-3-line text-accent-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">Compare Prices &amp; Quality</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">The spreadsheet includes QC (quality control) photo references and price comparisons, so you can evaluate product quality and value before committing to a purchase.</p>
              </div>
            </div>
            <div className="bg-white border-2 border-background-200/80 rounded-xl p-6 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-secondary-200 flex items-center justify-center flex-shrink-0">
                <i className="ri-global-line text-secondary-700 text-lg"></i>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">Estimate Shipping Costs</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Understand Basetao shipping methods, weight-based pricing, and destination-specific estimates so there are no surprises when your haul arrives at the warehouse.</p>
              </div>
            </div>
            <div className="bg-white border-2 border-background-200/80 rounded-xl p-6 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-user-line text-primary-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">Shop With Confidence</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">All products listed in the spreadsheet have been vetted by experienced shoppers. Warehouse QC photos provide an extra layer of verification before international shipping.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CategorySection />

      <section className="relative w-full py-16 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
            Ready to Start Shopping?
          </h2>
          <p className="text-foreground-600 text-base max-w-2xl mx-auto mb-10 leading-relaxed">
            Jump directly into the Basetao spreadsheet and explore thousands of curated product finds. Whether you are hunting for sneakers, streetwear, accessories, or home goods — the spreadsheet has something for every shopper.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto bg-primary-500 text-white font-semibold text-base px-10 py-4 rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40"
            >
              Explore Basetao Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto bg-foreground-900 text-white font-semibold text-base px-10 py-4 rounded-full hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </section>

      <PillarArticles />

      <section className="relative w-full py-16 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-foreground-600 text-base">Quick answers to the most common Basetao spreadsheet questions.</p>
          </div>
          <div className="space-y-3">
            {[
              { q: 'What exactly is the Basetao Spreadsheet?', a: 'The Basetao spreadsheet is a curated document that organizes product listings, prices, warehouse QC photos, and shipping estimates from Chinese marketplaces into one searchable resource. It helps international shoppers discover products efficiently without browsing hundreds of individual store pages.' },
              { q: 'Is the Basetao Spreadsheet free to use?', a: 'Yes, the spreadsheet itself is freely accessible. You can browse product listings, compare prices, and view QC photos without any cost. The only expenses are the actual product purchases and shipping fees when you decide to buy.' },
              { q: 'Is Basetao legit and safe to use?', a: 'Basetao is a legitimate shopping agent service that has served thousands of international customers. They provide warehouse storage, QC photo verification, and multiple secure shipping options. All transactions are protected through standard payment channels.' },
              { q: 'How much does Basetao shipping cost?', a: 'Basetao shipping costs depend on package weight, destination country, and shipping method (e.g., EMS, DHL, SAL, sea freight). Generally, shipping to the US and Europe ranges from $15 to $60 per kilogram depending on the speed and carrier you choose.' },
              { q: 'How do I find the best products on the Basetao spreadsheet?', a: 'Start by selecting a category that interests you, then browse the product listings with QC photos. Pay attention to price comparisons, sizing notes, and user feedback. The spreadsheet is organized to make filtering by category, price range, and popularity straightforward.' },
              { q: 'Does Basetao offer discount codes or coupons?', a: 'Yes, Basetao occasionally offers promotional discounts and shipping coupons. These are typically announced on their platform and through partner channels. The spreadsheet often includes notes about current promotions when available.' },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-white border-2 border-background-200/80 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors duration-200 list-none">
                  <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 group-open:rotate-180 transition-transform duration-300">
                    <i className="ri-arrow-down-s-line text-foreground-500"></i>
                  </div>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}